import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ButtonV2, H1, P, Spinner } from 'shared';
import { usePutServicingV1InstrumentsInstrumentIdAutopaySettingsMutation } from 'servicing-api';
import { setAutopayCompleted, setPaymentOption } from '../../actions';
import { DisclosureFooter, FlowActions } from '../../components';
import { useAuthorizationToken, useEffectiveBankAccountId, useFlexFinalizeData, useFlowPath, useSelectedBankAccount, } from '../../hooks';
import { selectors } from '../../reducer';
import { getAutopayBackRoute } from '../../utils';
import { PaymentOptionCard, SkipAutopayModal } from '../components';
function AutopayAuth() {
    const history = useHistory();
    const dispatch = useDispatch();
    const [isSkipAutopayModalOpen, setIsSkipAutopayModalOpen] = useState(false);
    const authorizationToken = useAuthorizationToken();
    const flowPath = useFlowPath(authorizationToken);
    const { instrumentId, lenderAccountIdentifier, finalizeData, isLoading: isFinalizeLoading, } = useFlexFinalizeData(authorizationToken);
    const selectedBankAccount = useSelector((state) => selectors.selectedBankAccount(state));
    const storedPaymentOption = useSelector((state) => selectors.paymentOption(state));
    const isDI = !!finalizeData?.suggestedMonthlyPayment;
    const selectedPaymentOption = storedPaymentOption ?? (isDI ? 'suggested' : 'minimum');
    const bankAccountId = useEffectiveBankAccountId(finalizeData);
    const bankAccount = useSelectedBankAccount(lenderAccountIdentifier, bankAccountId);
    const selectedBankAccountNumber = bankAccount?.accountNumberLastFour ||
        selectedBankAccount?.accountId ||
        finalizeData?.bankAccountLast4 ||
        '';
    const bankName = bankAccount?.bankName;
    const handlePaymentOptionChange = (option) => {
        dispatch(setPaymentOption(option));
    };
    const [putAutopaySettings, { isLoading }] = usePutServicingV1InstrumentsInstrumentIdAutopaySettingsMutation();
    const selectedAmount = selectedPaymentOption === 'suggested'
        ? finalizeData?.suggestedMonthlyPayment
        : finalizeData?.monthlyPayment;
    const goToNextStep = async () => {
        if (instrumentId && selectedAmount) {
            try {
                await putAutopaySettings({
                    instrumentId: Number(instrumentId),
                    putServicingV1InstrumentsInstrumentIdAutopaySettings: {
                        autopayEnabled: true,
                        autopayAmount: selectedAmount,
                        autopayDayOfMonth: finalizeData?.estimatedFirstPaymentDate
                            ? parseInt(finalizeData.estimatedFirstPaymentDate.split('-')[2], 10)
                            : undefined,
                        paymentOption: selectedPaymentOption,
                        ...(bankAccountId ? { bankAccountId } : {}),
                    },
                }).unwrap();
                dispatch(setAutopayCompleted(true));
                history.push(flowPath('/sign-agreement'));
            }
            catch (error) {
                // Handle error
            }
        }
        else {
            dispatch(setAutopayCompleted(true));
            history.push(flowPath('/sign-agreement'));
        }
    };
    if (isFinalizeLoading || !finalizeData) {
        return (_jsx("div", { className: "text-center", children: _jsx(Spinner, {}) }));
    }
    return (_jsxs("div", { className: "payment-authorization-container flex dir-col justify-center", children: [_jsxs("div", { className: "w100", children: [_jsx(H1, { variant: "headline-large", children: "Review Your Autopay Options" }), isDI && (_jsx(P, { variant: "title-large", className: "payment-authorization-subtitle mar-b-0", children: "Pick how much you'd like to pay each month. You can change this later." }))] }), _jsxs("div", { className: "autopay-auth-content flex dir-col align-center", children: [_jsx(PaymentOptionCard, { selectedBankAccountNumber: selectedBankAccountNumber, bankName: bankName, monthlyPayment: finalizeData?.monthlyPayment, suggestedMonthlyPayment: finalizeData?.suggestedMonthlyPayment, selectedPaymentOption: selectedPaymentOption, onPaymentOptionChange: handlePaymentOptionChange, disclosure: _jsx(DisclosureFooter, { className: "payment-option-card__disclosure", variant: "body-medium", termLength: finalizeData?.product?.termLength, loanAmount: finalizeData?.loanAmount, calculatedApr: finalizeData?.calculatedApr, promotionalPeriod: isDI ? finalizeData?.product?.promotionalPeriod : undefined }) }), _jsx(ButtonV2, { type: "button", variant: "text", color: "black", size: "medium", overwriteCasing: true, className: "mar-t-24", onClick: () => setIsSkipAutopayModalOpen(true), children: "Skip autopay and pay manually each month" }), _jsx(FlowActions, { secondary: _jsx(ButtonV2, { type: "button", variant: "ghost", color: "orange-flex", size: "medium", hugText: true, overwriteCasing: true, onClick: () => history.push(flowPath(getAutopayBackRoute(finalizeData))), children: "Back" }), children: _jsx(ButtonV2, { type: "button", variant: "filled", color: "orange-flex", size: "medium", hugText: true, overwriteCasing: true, onClick: () => goToNextStep(), submitting: isLoading, children: "Set Up Autopay" }) })] }), _jsx(SkipAutopayModal, { isOpen: isSkipAutopayModalOpen, setIsOpen: setIsSkipAutopayModalOpen, onContinueWithFirstPayment: () => history.push(flowPath('/autopay-auth/one-time-payment')) })] }));
}
export default AutopayAuth;
